import { Component, inject, input, output, signal, OnInit, LOCALE_ID, computed } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { LookupService } from '../../../../../services/pharmacy/lookup.service';
import { PharmacySegmentVTO } from '../../../../../services/pharmacy/lookup.model';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { PharmacyService } from '../../../../../services/pharmacy/pharmacy.service';
import { AcceptPharmacyVTO } from '../../../../../services/pharmacy/request.model';
import { HasScopeDirective } from '../../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../../services/scope/scope-constants';
import { AssignRepresentative } from '../../../../admin-customer-shared-components/create-pharmacy-component/assign-representative/assign-representative';

@Component({
  selector: 'app-approve-pharmacy-dialog',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, HasScopeDirective, AssignRepresentative],
  templateUrl: './approve-pharmacy-dialog.html',
    styleUrl: './approve-pharmacy-dialog.scss',
})
export class ApprovePharmacyDialog implements OnInit {
  pharmacyName = input<string>('');
  pharmacyId = input.required<number>();
  close = output();
  success = output();

  private lookupService = inject(LookupService);
  private pharmacyService = inject(PharmacyService);
  private localeId = inject(LOCALE_ID);

  readonly SCOPES = SCOPES;

  segments = signal<PharmacySegmentVTO[]>([]);
  governorates = signal<LookupVTO[]>([]);
  cities = signal<LookupVTO[]>([]);
  regions = signal<LookupVTO[]>([]);

  selectedSegmentId = signal<number | null>(null);
  selectedGovernorateId = signal<number | null>(null);
  selectedCityId = signal<number | null>(null);
  selectedRegionId = signal<number | null>(null);
  pharmacyQuota = signal<number | null>(null);


  representativeForm = new FormGroup({
    representative: new FormControl('', { validators: [Validators.required] }),
  });
  representativeResults = signal<{ id: number; fullName: string }[]>([]);
  showRepresentativeDropdown = signal<boolean>(false);
  loadingRepresentative = signal<boolean>(false);
  errorRepresentative = signal<boolean>(false);
  selectedRepresentativeId = signal<number | null>(null);

  commentControl = new FormControl('', [Validators.maxLength(2000)]);

  isSubmitting = signal<boolean>(false);
  showErrors = signal<boolean>(false);
  backendError = signal<string | null>(null);

  isArabic = computed(() => this.localeId.startsWith('ar'));

  ngOnInit() {
    this.lookupService.getPharmacySegments().subscribe((res) => this.segments.set(res.list));
    this.lookupService.getGovernorates().subscribe((res) => this.governorates.set(res.list));

    this.representativeForm.get('representative')?.valueChanges.subscribe((value) => {
      this.onRepresentativeSearchChange(value);
    });
  }

  onGovernorateChange(id: number) {
    this.selectedGovernorateId.set(id);
    this.selectedCityId.set(null);
    this.selectedRegionId.set(null);
    this.cities.set([]);
    this.regions.set([]);
    if (id) this.lookupService.getCities([Number(id)]).subscribe((res) => this.cities.set(res.list));
  }

  onCityChange(id: number) {
    this.selectedCityId.set(id);
    this.selectedRegionId.set(null);
    this.regions.set([]);
    if (id) this.lookupService.getRegions([Number(id)]).subscribe((res) => this.regions.set(res.list));
  }

  getLang(obj: any) {
    return this.isArabic() ? obj.titleAr : obj.titleEn;
  }

  getSegmentDisplay(s: any) {
    const name = this.getLang(s);
    const discount = s.extraDiscount || 0;
    return this.isArabic() ? `${name} (خصم ${discount}%)` : `${name} (${discount}% discount)`;
  }

  getQuotaError(): string | null {
    const val = this.pharmacyQuota();
    if (val === null || val === undefined) {
      return this.isArabic() ? 'الحد الائتماني مطلوب' : 'Credit Limit is required';
    }
    if (val < 0) {
      return this.isArabic() ? 'الحد الائتماني لا يمكن أن يكون سالباً' : 'Credit Limit cannot be negative';
    }
    if (val > 10000000) {
      return this.isArabic()
        ? 'الحد الائتماني لا يمكن أن يتجاوز 10,000,000.00 جنيه مصري'
        : 'Credit Limit cannot exceed 10,000,000.00 EGP';
    }
    return null;
  }

  getErrors() {
    return {
      segment: this.isArabic() ? 'يجب اختيار نوع الاشتراك' : 'Pharmacy Segment must be selected',
      gov: this.isArabic() ? 'المحافظة مطلوبة' : 'Governorate is required',
      city: this.isArabic() ? 'المدينة مطلوبة' : 'City is required',
      region: this.isArabic() ? 'المنطقة مطلوبة' : 'Region is required',
    };
  }

  getQuotaWarning(): string | null {
    if (this.pharmacyQuota() === 0) {
      return this.isArabic()
        ? 'تحذير: تعيين الحد الائتماني إلى صفر سيمنع هذه الصيدلية من تقديم طلبات بالائتمان'
        : 'Warning: Setting credit limit to zero will prevent this pharmacy from placing orders on credit';
    }
    return null;
  }

  onRepresentativeSearchChange(value: string | null) {
    this.selectedRepresentativeId.set(null);

    if (!value) {
      this.representativeResults.set([]);
      this.showRepresentativeDropdown.set(false);
      return;
    }

    this.loadingRepresentative.set(true);
    this.errorRepresentative.set(false);

    this.pharmacyService
      .pharmacyRepresentativeAutoComplete(value)
      .pipe(finalize(() => this.loadingRepresentative.set(false)))
      .subscribe({
        next: (res) => {
          this.showRepresentativeDropdown.set(true);
          this.representativeResults.set(res.list);
        },
        error: () => {
          this.errorRepresentative.set(true);
        },
      });
  }

  onSelectRepresentative(event: { id: string; fullName: string }) {
    const { id } = event;
    this.selectedRepresentativeId.set(Number(id));
    this.representativeForm.patchValue({ representative: id }, { emitEvent: false });
    this.showRepresentativeDropdown.set(false);
  }

  onClose() {
    this.close.emit();
  }

  onConfirm() {
    this.showErrors.set(true);
    this.backendError.set(null);
    this.commentControl.markAsTouched();
    this.representativeForm.get('representative')?.markAsTouched();

    const isValid =
      this.selectedSegmentId() &&
      this.getQuotaError() === null &&
      this.selectedGovernorateId() &&
      this.selectedCityId() &&
      this.selectedRegionId() &&
      this.selectedRepresentativeId() &&
      this.commentControl.valid;

    if (!isValid) return;

    this.isSubmitting.set(true);

    const payload: AcceptPharmacyVTO = {
      pharmacyCredit: Number(this.pharmacyQuota()!),
      pharmacySegmentId: Number(this.selectedSegmentId()!),
      governorateId: Number(this.selectedGovernorateId()!),
      cityId: Number(this.selectedCityId()!),
      regionId: Number(this.selectedRegionId()!),
      representativeId: Number(this.selectedRepresentativeId()!),
      comment: this.commentControl.value || undefined,
    };

    this.pharmacyService.acceptPharmacy(this.pharmacyId(), payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.success.emit();
          this.close.emit();
        },
        error: (err) => {
          this.backendError.set(
            this.isArabic() ? err.messageAr || 'حدث خطأ ما' : err.messageEn || 'An error occurred.'
          );
        },
      });
  }
}